import {
  FC,
  useMemo,
  useCallback,
  useState,
  MouseEvent,
  useRef,
  SyntheticEvent,
  useEffect,
} from 'react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.min.css';
import {
  IServerSideDatasource,
  GridReadyEvent,
  ColDef,
  PaginationChangedEvent,
  ValueFormatterParams,
  KeyCreatorParams,
  GetContextMenuItems,
  FilterChangedEvent,
  FilterModel,
  GridApi,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import Paper from '@mui/material/Paper';
import { useMutation } from '@tanstack/react-query';
import { getOrdersList, downloadOrders } from '../http/orders';
import {
  CategoryType,
  ClientType,
  CommercialPlan,
  CommercialSubcategoryType,
  CouponStatus,
  OrderDto,
  OrderStatus,
  QuotationType,
  ResidentialSubcategoryType,
  locale,
  localizeDateTime,
} from 'ventas-digitales-v2-common';
import UUIDRenderer from './CellRenderers/UUIDRenderer';
import OrderStatusRenderer from './CellRenderers/OrderStatusRenderer';
import ActionsRenderer from './CellRenderers/ActionsRenderer';
import {
  DEFAULT_COL,
  NUMERIC_FILTER_PARAMS,
  PAGE_SIZE_SELECTOR,
  TEXT_FILTER_PARAMS,
  dateFilterParams,
  parseFilters,
} from '../utils/agGrid';
import { useRouter } from 'next/navigation';
import OrderStatusFilterRenderer from './CellRenderers/OrderStatusFilterRenderer';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import RestoreIcon from '@mui/icons-material/Restore';
import DownloadOrdersModal from './modals/DownloadOrdersModal';
import toast from 'react-hot-toast';
import { APP_NAME } from '../appName';
import useUser from '../auth/useUser';

const INITIAL_STATUS_FILTER: FilterModel = {
  values: [
    OrderStatus.Signed,
    OrderStatus.Completed,
    OrderStatus.PendingGIS,
    OrderStatus.PendingGIS2,
    OrderStatus.RejectedGIS,
    OrderStatus.ClosedGIS,
    OrderStatus.PendingContractor,
    OrderStatus.RejectedContractor,
    OrderStatus.PendingCertificate,
    OrderStatus.PendingValidation,
    OrderStatus.RejectedValidation,
    OrderStatus.PendingCreation,
    OrderStatus.PendingOsfQuotation,
    OrderStatus.ErrorOsfQuotation,
    OrderStatus.Created,
    OrderStatus.ReadyToCreate,
    OrderStatus.CreationError,
    OrderStatus.Failed,
  ],
  filterType: 'set',
};

const DEFAULT_COLUMNS: ColDef<OrderDto>[] = [
  {
    field: 'id',
    headerName: 'ID',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    tooltipField: 'id',
    width: 150,
    minWidth: 150,
    maxWidth: 150,
    cellRenderer: UUIDRenderer,
    resizable: false,
    pinned: true,
    lockPosition: true,
  },
  {
    field: 'orderId',
    headerName: 'Orden OSF',
    filter: 'agNumberColumnFilter',
    filterParams: NUMERIC_FILTER_PARAMS,
    width: 140,
    minWidth: 140,
    valueGetter({ data }) {
      return data?.orderId ?? '-';
    },
  },
  {
    field: 'consecutive',
    headerName: 'Consecutivo',
    filter: 'agNumberColumnFilter',
    filterParams: NUMERIC_FILTER_PARAMS,
    width: 140,
    minWidth: 140,
    maxWidth: 140,
    resizable: false,
    lockPosition: true,
    valueGetter({ data }) {
      return data?.consecutive ?? '-';
    },
  },
  {
    field: 'status',
    headerName: 'Estado',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSorting: true,
      debounceMs: 500,
      cellRenderer: OrderStatusFilterRenderer,
      values: [
        /** Action-required statuses */
        {
          id: OrderStatus.RejectedGIS,
          label: locale.orderStatusEnum[OrderStatus.RejectedGIS],
        },
        {
          id: OrderStatus.PendingContractor,
          label: locale.orderStatusEnum[OrderStatus.PendingContractor],
        },
        {
          id: OrderStatus.PendingValidation,
          label: locale.orderStatusEnum[OrderStatus.PendingValidation],
        },
        {
          id: OrderStatus.CreationError,
          label: locale.orderStatusEnum[OrderStatus.CreationError],
        },
        {
          id: OrderStatus.ErrorOsfQuotation,
          label: locale.orderStatusEnum[OrderStatus.ErrorOsfQuotation],
        },
        /** Final/error statuses */
        {
          id: OrderStatus.ClosedGIS,
          label: locale.orderStatusEnum[OrderStatus.ClosedGIS],
        },
        {
          id: OrderStatus.RejectedContractor,
          label: locale.orderStatusEnum[OrderStatus.RejectedContractor],
        },
        {
          id: OrderStatus.RejectedValidation,
          label: locale.orderStatusEnum[OrderStatus.RejectedValidation],
        },
        {
          id: OrderStatus.Created,
          label: locale.orderStatusEnum[OrderStatus.Created],
        },
        {
          id: OrderStatus.ReadyToCreate,
          label: locale.orderStatusEnum[OrderStatus.ReadyToCreate],
        },
        {
          id: OrderStatus.Failed,
          label: locale.orderStatusEnum[OrderStatus.Failed],
        },
        /** Transition statuses */
        {
          id: OrderStatus.PendingGIS,
          label: locale.orderStatusEnum[OrderStatus.PendingGIS],
        },
        {
          id: OrderStatus.PendingGIS2,
          label: locale.orderStatusEnum[OrderStatus.PendingGIS2],
        },
        {
          id: OrderStatus.PendingCertificate,
          label: locale.orderStatusEnum[OrderStatus.PendingCertificate],
        },
        {
          id: OrderStatus.PendingCreation,
          label: locale.orderStatusEnum[OrderStatus.PendingCreation],
        },
        {
          id: OrderStatus.PendingOsfQuotation,
          label: locale.orderStatusEnum[OrderStatus.PendingOsfQuotation],
        },
        /** Mobile statuses */
        {
          id: OrderStatus.Pending,
          label: locale.orderStatusEnum[OrderStatus.Pending],
        },
        {
          id: OrderStatus.InProgress,
          label: locale.orderStatusEnum[OrderStatus.InProgress],
        },
        {
          id: OrderStatus.Signed,
          label: locale.orderStatusEnum[OrderStatus.Signed],
        },
        {
          id: OrderStatus.Completed,
          label: locale.orderStatusEnum[OrderStatus.Completed],
        },
        {
          id: OrderStatus.Deassigned,
          label: locale.orderStatusEnum[OrderStatus.Deassigned],
        },
        {
          id: OrderStatus.Closed,
          label: locale.orderStatusEnum[OrderStatus.Closed],
        },
        {
          id: OrderStatus.CancelledOSF,
          label: locale.orderStatusEnum[OrderStatus.CancelledOSF],
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      return data ? (locale.orderStatusEnum[data.status] ?? data.status) : '-';
    },
    cellRenderer: OrderStatusRenderer,
  },
  {
    colId: 'type',
    headerName: 'Tipo de Venta',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressMiniFilter: true,
      suppressSorting: true,
      debounceMs: 500,
      values: [
        {
          id: QuotationType.Residential,
          label: locale.quotationTypeEnum[QuotationType.Residential],
        },
        {
          id: QuotationType.SpecialResidential,
          label: locale.quotationTypeEnum[QuotationType.SpecialResidential],
        },
        {
          id: QuotationType.Commercial,
          label: locale.quotationTypeEnum[QuotationType.Commercial],
        },
        {
          id: QuotationType.SpecialCommercial,
          label: locale.quotationTypeEnum[QuotationType.SpecialCommercial],
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 180,
    minWidth: 180,
    valueGetter({ data }) {
      const quotation = data?.residentialQuotation || data?.commercialQuotation;

      if (!quotation) {
        return '-';
      }

      return locale.quotationTypeEnum[quotation.type];
    },
  },
  {
    colId: 'commercialPlan',
    headerName: 'Plan Comercial',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressMiniFilter: true,
      suppressSorting: true,
      showTooltips: true,
      debounceMs: 500,
      values: [
        {
          id: CommercialPlan.ResidentialFullPackage,
          label: `${CommercialPlan.ResidentialFullPackage} - ${locale.commercialPlanEnum[CommercialPlan.ResidentialFullPackage]}`,
        },
        {
          id: CommercialPlan.ResidentialCertifiedConnectionFee,
          label: `${CommercialPlan.ResidentialCertifiedConnectionFee} - ${locale.commercialPlanEnum[CommercialPlan.ResidentialCertifiedConnectionFee]}`,
        },
        {
          id: CommercialPlan.ResidentialInternalConnectionFee,
          label: `${CommercialPlan.ResidentialInternalConnectionFee} - ${locale.commercialPlanEnum[CommercialPlan.ResidentialInternalConnectionFee]}`,
        },
        {
          id: CommercialPlan.ResidentialConnectionFee,
          label: `${CommercialPlan.ResidentialConnectionFee} - ${locale.commercialPlanEnum[CommercialPlan.ResidentialConnectionFee]}`,
        },
        {
          id: CommercialPlan.SpecialResidentialFullPackage,
          label: `${CommercialPlan.SpecialResidentialFullPackage} - ${locale.commercialPlanEnum[CommercialPlan.SpecialResidentialFullPackage]}`,
        },
        {
          id: CommercialPlan.SpecialResidentialCertifiedConnectionFee,
          label: `${CommercialPlan.SpecialResidentialCertifiedConnectionFee} - ${locale.commercialPlanEnum[CommercialPlan.SpecialResidentialCertifiedConnectionFee]}`,
        },
        {
          id: CommercialPlan.SpecialResidentialConnectionFee,
          label: `${CommercialPlan.SpecialResidentialConnectionFee} - ${locale.commercialPlanEnum[CommercialPlan.SpecialResidentialConnectionFee]}`,
        },
        {
          id: CommercialPlan.CommercialFullPackage,
          label: `${CommercialPlan.CommercialFullPackage} - ${locale.commercialPlanEnum[CommercialPlan.CommercialFullPackage]}`,
        },
        {
          id: CommercialPlan.CommercialCertifiedConnectionFee,
          label: `${CommercialPlan.CommercialCertifiedConnectionFee} - ${locale.commercialPlanEnum[CommercialPlan.CommercialCertifiedConnectionFee]}`,
        },
        {
          id: CommercialPlan.CommercialConnectionFee,
          label: `${CommercialPlan.CommercialConnectionFee} - ${locale.commercialPlanEnum[CommercialPlan.CommercialConnectionFee]}`,
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 200,
    minWidth: 200,
    valueGetter({ data }) {
      const quotation = data?.residentialQuotation || data?.commercialQuotation;

      if (!quotation) {
        return '-';
      }

      return `${quotation.commercialPlan} - ${locale.commercialPlanEnum[quotation.commercialPlan]}`;
    },
  },
  {
    colId: 'financed',
    sortable: false,
    headerName: 'Forma de Pago',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressMiniFilter: true,
      suppressSorting: true,
      values: [
        {
          id: true,
          label: 'Financiada',
        },
        {
          id: false,
          label: 'No Financiada',
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 140,
    minWidth: 140,
    maxWidth: 140,
    valueGetter({ data }) {
      const quotation = data?.residentialQuotation || data?.commercialQuotation;

      if (!quotation) {
        return '-';
      }

      return quotation.financed ? 'Financiada' : 'No Financiada';
    },
  },
  {
    colId: 'subsidized',
    sortable: false,
    headerName: 'Subsidiada',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressMiniFilter: true,
      suppressSorting: true,
      values: [
        {
          id: true,
          label: 'Subsidada',
        },
        {
          id: false,
          label: 'No Subsidiada',
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 140,
    minWidth: 140,
    maxWidth: 140,
    valueGetter({ data }) {
      if (!data?.residentialQuotation) {
        return '-';
      }

      const { subsidies } = data.residentialQuotation;

      return subsidies.length > 0 ? 'Subsidiada' : 'No Subsidiada';
    },
  },
  {
    colId: 'deal',
    headerName: 'Convenio',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 200,
    minWidth: 200,
    valueGetter({ data }) {
      if (!data?.residentialQuotation) {
        return '-';
      }

      const { subsidies } = data.residentialQuotation;

      return subsidies.length > 0 ? subsidies[0].deal : '-';
    },
  },
  {
    field: 'createdAt',
    headerName: 'Fecha de Inicio',
    sort: 'desc',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams(),
    width: 200,
    minWidth: 200,
    valueGetter({ data }) {
      return data ? localizeDateTime(data.createdAt) : '-';
    },
  },
  {
    field: 'osfCreationDate',
    headerName: 'Fecha de Creación en OSF',
    sort: 'desc',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams(),
    width: 220,
    minWidth: 220,
    valueGetter({ data }) {
      return data ? localizeDateTime(data.osfCreationDate) : '-';
    },
  },
  {
    field: 'completedAt',
    headerName: 'Fecha de Finalización',
    sort: 'desc',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams(),
    width: 220,
    minWidth: 220,
    valueGetter({ data }) {
      return data ? localizeDateTime(data.completedAt) : '-';
    },
  },
  {
    field: 'contractId',
    headerName: 'Contrato',
    filter: 'agNumberColumnFilter',
    filterParams: NUMERIC_FILTER_PARAMS,
    width: 160,
    minWidth: 160,
    valueGetter({ data }) {
      return data?.contractId ?? '-';
    },
  },
  {
    colId: 'clientType',
    sortable: false,
    headerName: 'Tipo de Cliente',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressMiniFilter: true,
      suppressSorting: true,
      showTooltips: true,
      debounceMs: 500,
      values: [
        {
          id: ClientType.NaturalPerson,
          label: locale.clientTypeEnum[ClientType.NaturalPerson],
        },
        {
          id: ClientType.LegalPerson,
          label: locale.clientTypeEnum[ClientType.LegalPerson],
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 160,
    minWidth: 160,
    valueGetter({ data }) {
      if (!data?.client) {
        return '-';
      }

      return locale.clientTypeEnum[data.client.clientType];
    },
  },
  {
    colId: 'client',
    headerName: 'Cliente',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 200,
    minWidth: 200,
    valueGetter({ data }) {
      if (!data?.client) {
        return '-';
      }

      const { clientType, fullName, businessName } = data.client;

      return clientType === 1 ? `${businessName} - ${fullName}` : fullName;
    },
  },
  {
    colId: 'identification',
    headerName: 'Identificación Cliente',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 170,
    minWidth: 170,
    valueGetter({ data }) {
      if (!data?.client) {
        return '-';
      }

      const { clientType, identification, businessIdentification } =
        data.client;

      return clientType === 1 ? businessIdentification : identification;
    },
  },
  {
    colId: 'cellphoneNumber',
    headerName: 'Teléfono del Cliente',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 160,
    minWidth: 160,
    valueGetter({ data }) {
      if (!data?.client) {
        return '-';
      }

      return data.client.cellphoneNumber;
    },
  },
  {
    colId: 'couponNumber',
    headerName: 'Cupón',
    filter: 'agNumberColumnFilter',
    filterParams: NUMERIC_FILTER_PARAMS,
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      return data?.coupon?.couponNumber ?? '-';
    },
  },
  {
    colId: 'couponStatus',
    headerName: 'Estado Cupón',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSorting: true,
      values: [
        {
          id: CouponStatus.Generated,
          label: locale.couponStatusEnum[CouponStatus.Generated],
        },
        {
          id: CouponStatus.Sent,
          label: locale.couponStatusEnum[CouponStatus.Sent],
        },
        {
          id: CouponStatus.Paid,
          label: locale.couponStatusEnum[CouponStatus.Paid],
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      if (!data?.coupon?.status) {
        return '-';
      }

      return locale.couponStatusEnum[data.coupon.status];
    },
  },
  {
    colId: 'paymentDate',
    headerName: 'Fecha de Pago Cupón',
    sort: 'desc',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams(),
    width: 220,
    minWidth: 220,
    valueGetter({ data }) {
      if (!data?.coupon?.paymentDate) {
        return '-';
      }

      return localizeDateTime(data.coupon.paymentDate);
    },
  },
  {
    colId: 'operatingUnit',
    headerName: 'Unidad Operativa',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 200,
    minWidth: 200,
    valueGetter({ data }) {
      return data?.technical?.customParameters?.operatingUnit ?? '-';
    },
  },
  {
    colId: 'technical',
    headerName: 'Técnico',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      return data?.technical ? (data.technical.email ?? '-') : '-';
    },
  },
  {
    colId: 'department',
    headerName: 'Departamento',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      return data?.gisAddress?.department ?? '-';
    },
  },
  {
    colId: 'locality',
    headerName: 'Localidad',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      return data?.gisAddress?.locality ?? '-';
    },
  },
  {
    colId: 'sector',
    headerName: 'Sector',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      return data?.gisAddress?.sector ?? '-';
    },
  },
  {
    colId: 'fullAddress',
    headerName: 'Dirección',
    filter: 'agTextColumnFilter',
    filterParams: TEXT_FILTER_PARAMS,
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      return data?.gisAddress?.fullAddress ?? '-';
    },
  },
  {
    colId: 'categoryId',
    headerName: 'Categoría',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressMiniFilter: true,
      suppressSorting: true,
      debounceMs: 500,
      values: [
        {
          id: CategoryType.Residential,
          label: locale.categoryTypeEnum[CategoryType.Residential],
        },
        {
          id: CategoryType.Commercial,
          label: locale.categoryTypeEnum[CategoryType.Commercial],
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      const categoryId = data?.gisAddress?.categoryId;

      if (!categoryId) {
        return '-';
      }

      return locale.categoryTypeEnum[categoryId];
    },
  },
  {
    colId: 'subcategoryId',
    headerName: 'Subcategoría',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressMiniFilter: true,
      suppressSorting: true,
      debounceMs: 500,
      values: [
        {
          id: `${CategoryType.Residential}-${ResidentialSubcategoryType.Stratum1}`,
          label:
            locale.residentialSubcategoryEnum[
              ResidentialSubcategoryType.Stratum1
            ],
        },
        {
          id: `${CategoryType.Residential}-${ResidentialSubcategoryType.Stratum2}`,
          label:
            locale.residentialSubcategoryEnum[
              ResidentialSubcategoryType.Stratum2
            ],
        },
        {
          id: `${CategoryType.Residential}-${ResidentialSubcategoryType.Stratum3}`,
          label:
            locale.residentialSubcategoryEnum[
              ResidentialSubcategoryType.Stratum3
            ],
        },
        {
          id: `${CategoryType.Residential}-${ResidentialSubcategoryType.Stratum4}`,
          label:
            locale.residentialSubcategoryEnum[
              ResidentialSubcategoryType.Stratum4
            ],
        },
        {
          id: `${CategoryType.Residential}-${ResidentialSubcategoryType.Stratum5}`,
          label:
            locale.residentialSubcategoryEnum[
              ResidentialSubcategoryType.Stratum5
            ],
        },
        {
          id: `${CategoryType.Residential}-${ResidentialSubcategoryType.Stratum6}`,
          label:
            locale.residentialSubcategoryEnum[
              ResidentialSubcategoryType.Stratum6
            ],
        },
        {
          id: `${CategoryType.Commercial}-${CommercialSubcategoryType.OnlyComercial}`,
          label:
            locale.commercialSubcategoryEnum[
              CommercialSubcategoryType.OnlyComercial
            ],
        },
        {
          id: `${CategoryType.Commercial}-${CommercialSubcategoryType.FixedFactor}`,
          label:
            locale.commercialSubcategoryEnum[
              CommercialSubcategoryType.FixedFactor
            ],
        },
        {
          id: `${CategoryType.Commercial}-${CommercialSubcategoryType.Special}`,
          label:
            locale.commercialSubcategoryEnum[CommercialSubcategoryType.Special],
        },
        {
          id: `${CategoryType.Commercial}-${CommercialSubcategoryType.Official}`,
          label:
            locale.commercialSubcategoryEnum[
              CommercialSubcategoryType.Official
            ],
        },
      ],
      keyCreator({ value }: KeyCreatorParams) {
        return value.id;
      },
      valueFormatter({ value }: ValueFormatterParams) {
        return value.label;
      },
    },
    width: 150,
    minWidth: 150,
    valueGetter({ data }) {
      const { categoryId, subcategoryId } = data?.gisAddress ?? {};

      if (!categoryId || !subcategoryId) {
        return '-';
      }

      if (categoryId === CategoryType.Residential) {
        return locale.residentialSubcategoryEnum[
          subcategoryId as ResidentialSubcategoryType
        ];
      }

      return locale.commercialSubcategoryEnum[
        subcategoryId as CommercialSubcategoryType
      ];
    },
  },
];

enum OrdersTab {
  ALL = 'ALL',
  RESIDENTIAL = 'RESIDENTIAL',
  COMMERCIAL = 'COMMERCIAL',
  SPECIAL_RESIDENTIAL = 'SPECIAL_RESIDENTIAL',
}
interface OrdersTableProps {
  operatingUnitsLabel: string | null;
}

const DEFAULT_PAGE_SIZE = 20;

const OrdersTable: FC<OrdersTableProps> = ({ operatingUnitsLabel }) => {
  const router = useRouter();
  const user = useUser();

  const { mutateAsync } = useMutation({ mutationFn: getOrdersList });

  const gridApiRef = useRef<GridApi | null>(null);

  const [activeTab, setActiveTab] = useState<OrdersTab>(OrdersTab.ALL);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);

  const { mutateAsync: generateCsv, isPending: isDownloadingCsv } = useMutation(
    { mutationFn: downloadOrders }
  );

  const onGenerateCsv = useCallback(
    async (startDate: string, endDate: string) => {
      const gridApi = gridApiRef.current;
      if (!gridApi) {
        return;
      }
      const filters = parseFilters(gridApi.getFilterModel());

      await generateCsv({
        filters: JSON.stringify(filters),
        startDate,
        endDate,
      });
      toast.success('¡Descarga exitosa!');
      setIsCsvModalOpen(false);
    },
    [generateCsv]
  );

  useEffect(() => {
    const savedTab = localStorage.getItem('ORDERS_LAST_ACTIVE_TAB');

    if (savedTab) {
      setActiveTab(savedTab as OrdersTab);
    }
  }, []);

  const onOpenItem = useCallback(
    (e: MouseEvent<HTMLButtonElement>, order: OrderDto) => {
      const route = `/orders/${order.id}`;

      if (e.ctrlKey) {
        window.open(route, '_blank');
      } else {
        router.push(route);
      }
    },
    [router]
  );

  const columns = useMemo<ColDef<OrderDto>[]>(() => {
    const filteredDefaultColumns =
      activeTab === OrdersTab.ALL
        ? DEFAULT_COLUMNS.filter((col) => col.colId !== 'type')
        : DEFAULT_COLUMNS;

    return [
      {
        colId: 'actions',
        width: 70,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: ActionsRenderer,
        resizable: false,
        pinned: true,
        lockPosition: true,
        sortable: false,
        cellRendererParams: { onClick: onOpenItem },
        contextMenuItems: [],
      },
      ...filteredDefaultColumns,
    ];
  }, [onOpenItem, activeTab]);

  const getTypeFilterForTab = (tab: OrdersTab): FilterModel | null => {
    switch (tab) {
      case OrdersTab.RESIDENTIAL:
        return {
          filterType: 'set',
          values: [QuotationType.Residential],
        };
      case OrdersTab.COMMERCIAL:
        return {
          filterType: 'set',
          values: [QuotationType.Commercial],
        };
      case OrdersTab.SPECIAL_RESIDENTIAL:
        return {
          filterType: 'set',
          values: [QuotationType.SpecialResidential],
        };
      case OrdersTab.ALL:
      default:
        return null;
    }
  };

  const getSavedFilters = () => {
    const saved = localStorage.getItem('ORDERS_FILTERS');
    return saved ? JSON.parse(saved) : {};
  };

  const getSavedColumnState = () => {
    const saved = localStorage.getItem('ORDERS_COLUMN_STATE');
    return saved ? JSON.parse(saved) : {};
  };

  const getPageSizes = () => {
    const saved = localStorage.getItem('ORDERS_PAGE_SIZE');
    return saved ? JSON.parse(saved) : {};
  };

  const resetSort = () => {
    gridApiRef.current?.applyColumnState({
      defaultState: {
        sort: null,
      },
    });
  };

  const handleTabChange = (_: SyntheticEvent, value: OrdersTab) => {
    setActiveTab(value);
    localStorage.setItem('ORDERS_LAST_ACTIVE_TAB', value);
  };

  useEffect(() => {
    if (!gridApiRef.current) {
      return;
    }

    const savedFilters: FilterModel = getSavedFilters()[activeTab] || {};
    const typeFilter = getTypeFilterForTab(activeTab);
    const updatedFilters = {
      ...savedFilters,
      status: savedFilters.status ?? INITIAL_STATUS_FILTER,
      ...(typeFilter ? { type: typeFilter } : {}),
    };
    gridApiRef.current?.setFilterModel(updatedFilters);

    const savedColumnState = getSavedColumnState()[activeTab];
    if (savedColumnState?.length) {
      gridApiRef.current?.applyColumnState({
        state: savedColumnState,
        applyOrder: true,
      });
    } else {
      resetSort();
    }

    const currentPageSize = getPageSizes()[activeTab];
    const newPageSize = currentPageSize
      ? Number(currentPageSize)
      : DEFAULT_PAGE_SIZE;
    setPageSize(newPageSize);
  }, [activeTab]);

  const datasource = useMemo<IServerSideDatasource>(
    () => ({
      async getRows({ success, request }) {
        const {
          startRow = 0,
          endRow = DEFAULT_PAGE_SIZE,
          sortModel: [sortModel],
          filterModel,
        } = request;
        const limit = endRow - startRow;
        const page = Math.floor(startRow / limit) + 1;

        const { colId, sort } = sortModel || {};
        const { orders, total } = await mutateAsync({
          limit,
          page,
          sort: colId ? `${colId}:${sort}` : '',
          filters: JSON.stringify(parseFilters(filterModel)),
        });

        success({
          rowData: orders,
          rowCount: total,
        });
      },
    }),
    [mutateAsync]
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api;

      const currentFilters = getSavedFilters()[activeTab];
      if (currentFilters) {
        if (!currentFilters.status) {
          event.api.setFilterModel({
            ...currentFilters,
            status: INITIAL_STATUS_FILTER,
          });
        } else {
          event.api.setFilterModel(currentFilters);
        }
      } else {
        event.api.setFilterModel({
          status: INITIAL_STATUS_FILTER,
        });
      }

      const currentColumnState = getSavedColumnState()[activeTab];
      if (currentColumnState) {
        event.api.applyColumnState({
          state: currentColumnState,
          applyOrder: true,
        });
      }

      event.api.setGridOption('serverSideDatasource', datasource);
    },
    [activeTab, datasource]
  );

  const [pageSize, setPageSize] = useState(() => {
    const currentPageSize = getPageSizes()[activeTab];
    return currentPageSize ? Number(currentPageSize) : DEFAULT_PAGE_SIZE;
  });

  const onPaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      if (event.newPageSize) {
        const savedPageSizes = getPageSizes();
        setPageSize(event.api.paginationGetPageSize());
        localStorage.setItem(
          'ORDERS_PAGE_SIZE',
          JSON.stringify({
            ...savedPageSizes,
            [activeTab]: event.api.paginationGetPageSize().toString(),
          })
        );
      }
    },
    [activeTab]
  );

  const canDownloadOrders = user?.roles.includes(`${APP_NAME}/download-orders`);

  const getContextMenuItems = useCallback<GetContextMenuItems<OrderDto>>(
    ({ node }) => {
      if (node?.data?.id) {
        return [
          'copy',
          'autoSizeAll',
          'separator',
          {
            name: 'Abrir en una nueva pestaña',
            action() {
              window.open(`/orders/${node?.data?.id}`, '_blank');
            },
          },
          ...(canDownloadOrders
            ? [
                {
                  name: 'Exportar',
                  icon: '<span class="ag-icon ag-icon-save" role="presentation" unselectable="on"></span>',
                  action: () => {
                    setIsCsvModalOpen(true);
                  },
                },
              ]
            : []),
        ];
      }

      return ['autoSizeAll'];
    },
    [canDownloadOrders]
  );

  const onColumnStateChanged = useCallback(
    ({ api }: { api: GridApi }) => {
      const currentColumnState = getSavedColumnState();

      localStorage.setItem(
        'ORDERS_COLUMN_STATE',
        JSON.stringify({
          ...currentColumnState,
          [activeTab]: api.getColumnState(),
        })
      );
    },
    [activeTab]
  );

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filters = e.api.getFilterModel();
      const current = getSavedFilters();

      localStorage.setItem(
        'ORDERS_FILTERS',
        JSON.stringify({
          ...current,
          [activeTab]: filters,
        })
      );
    },
    [activeTab]
  );

  const onClearFilters = useCallback(() => {
    const gridApi = gridApiRef.current;
    if (gridApi) {
      const typeFilter = getTypeFilterForTab(activeTab);
      gridApi.setFilterModel({
        status: INITIAL_STATUS_FILTER,
        ...(typeFilter ? { type: typeFilter } : {}),
      });
      const currentFilters = getSavedFilters();
      localStorage.setItem(
        'ORDERS_FILTERS',
        JSON.stringify({
          ...currentFilters,
          [activeTab]: gridApi.getFilterModel(),
        })
      );
      resetSort();
    }
  }, [activeTab]);

  const onRestoreColumnState = useCallback(() => {
    const gridApi = gridApiRef.current;

    if (!gridApi) {
      return;
    }

    const sortState = gridApi
      .getColumnState()
      .filter(({ sort }) => sort != null)
      .map(({ colId, sort, sortIndex }) => ({
        colId,
        sort,
        sortIndex,
      }));

    const currentColumnState = getSavedColumnState();
    const { [activeTab]: _, ...restColumnState } = currentColumnState;

    localStorage.setItem(
      'ORDERS_COLUMN_STATE',
      JSON.stringify(restColumnState)
    );
    gridApi.resetColumnState();

    gridApi.applyColumnState({
      state: sortState,
      defaultState: {
        sort: null,
        sortIndex: null,
      },
    });
  }, [activeTab]);

  return (
    <>
      <Typography variant="overline" fontSize={15} fontWeight="bold">
        listado de ventas{operatingUnitsLabel}
      </Typography>
      <Paper
        className="ag-theme-quartz"
        sx={{
          fontFamily: 'Roboto',
          height: 'calc(100vh - 250px)',
          minHeight: 500,
          mt: 1,
          overflow: 'clip',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Todas" value={OrdersTab.ALL} />
            <Tab label="Residenciales" value={OrdersTab.RESIDENTIAL} />
            <Tab label="Comerciales" value={OrdersTab.COMMERCIAL} />
            <Tab
              label="Residenciales especiales"
              value={OrdersTab.SPECIAL_RESIDENTIAL}
            />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1, marginRight: 2 }}>
            <Button
              variant="outlined"
              color="info"
              onClick={onClearFilters}
              size="small"
              startIcon={<CleaningServicesIcon fontSize="small" />}
            >
              <span style={{ padding: 0 }}>Limpiar filtros</span>
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={onRestoreColumnState}
              size="small"
              startIcon={<RestoreIcon fontSize="small" />}
            >
              <span style={{ padding: 0 }}>Restaurar columnas</span>
            </Button>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <AgGridReact
            onGridReady={onGridReady}
            columnDefs={columns}
            rowModelType="serverSide"
            pagination
            paginationPageSize={pageSize}
            cacheBlockSize={pageSize}
            paginationPageSizeSelector={PAGE_SIZE_SELECTOR}
            onPaginationChanged={onPaginationChanged}
            animateRows
            tooltipShowDelay={200}
            defaultColDef={DEFAULT_COL}
            localeText={AG_GRID_LOCALE_ES}
            getContextMenuItems={getContextMenuItems}
            onSortChanged={onColumnStateChanged}
            onColumnMoved={onColumnStateChanged}
            onColumnVisible={onColumnStateChanged}
            onColumnResized={onColumnStateChanged}
            onColumnPinned={onColumnStateChanged}
            onFilterChanged={onFilterChanged}
            containerStyle={{ padding: 10, height: '100%' }}
          />
        </Box>
        <DownloadOrdersModal
          open={isCsvModalOpen}
          isLoading={isDownloadingCsv}
          onClose={() => setIsCsvModalOpen(false)}
          onGenerate={onGenerateCsv}
        />
      </Paper>
    </>
  );
};

export default OrdersTable;
